import { useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import axios from "axios";
import { Button, Card, CardBody } from "@heroui/react";
import { ArrowLeft } from "lucide-react";
import {
  PaymentSelectionStep,
  type PaymentStepHandle,
} from "./steps/payment-selection-step";
import { CreateShell } from "./create-order-layout";
import { useCreateOrder } from "./create-order-context";
import { pickupDisplayCents } from "./pickup-pricing";
import { DraftSavedIndicator } from "./draft-saved-indicator";
import { marketingUrl } from "@/services/marketing";
import { useCreateUnifiedOrder } from "@/api/use-unified-order";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/services/logger";
import { totalWeightKg } from "@/lib/unit-display";
import type { UnifiedOrderRequest, Order } from "@/api.types.v2";
import type { OrderDraft, PackageFormData } from "./types";
import { useTranslation } from "react-i18next";

function formatCurrency(cents?: number, currency = "CAD"): string {
  if (cents === undefined) return "$0.00";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency,
  }).format(cents / 100);
}

function OrderSummaryCard({ data }: { data: OrderDraft }) {
  const { t } = useTranslation("app");
  const shipment = data.shipment;
  const quote = data.selectedQuote;
  if (!shipment || !quote) return null;

  const labelCents = quote.labelPriceCents;
  const labelTaxCents = quote.taxCents ?? 0;
  const pickupTier =
    data.includePickup && data.pickup
      ? quote.pickupOptions.find(
          (t) => t.pickupServiceId === data.pickup?.pickupServiceId
        )
      : undefined;
  const {
    pickupCents,
    discountCents: pickupDiscountCents,
    taxCents: pickupTaxCents,
  } = pickupDisplayCents(
    pickupTier,
    data.pickupBundleDiscountCents ?? 0,
    !!(data.includePickup && data.pickup)
  );
  const subtotalCents = labelCents + pickupCents - pickupDiscountCents;
  const taxCents = labelTaxCents + pickupTaxCents;
  const grandTotalCents = subtotalCents + taxCents;
  const currency = quote.currency;

  const totalKg = totalWeightKg(shipment.packages).toFixed(1);
  const firstPkg = shipment.packages[0];
  const dims =
    firstPkg?.length && firstPkg.width && firstPkg.height
      ? `${firstPkg.length}×${firstPkg.width}×${firstPkg.height} ${firstPkg.dimensionUnit}`
      : "";

  return (
    <div className="sticky top-4">
      <Card shadow="none" className="border border-default-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-default-200">
          <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
            {t("createShipment.orderSummary")}
          </div>
          <div className="text-base font-bold mt-1.5">
            {quote.carrierService.name}
          </div>
          <div className="text-sm text-default-500">{quote.carrier.name}</div>
        </div>

        <CardBody className="px-6 py-4 gap-3.5 text-sm">
          <div className="flex justify-between">
            <span className="text-default-600">{t("createShipment.from")}</span>
            <span className="font-medium text-right">
              {shipment.senderName || "—"}
              <br />
              <span className="text-default-500 font-mono text-xs">
                {shipment.shipFrom.city}, {shipment.shipFrom.state}
              </span>
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-default-600">{t("createShipment.to")}</span>
            <span className="font-medium text-right">
              {shipment.recipientName || "—"}
              <br />
              <span className="text-default-500 font-mono text-xs">
                {shipment.shipTo.city}, {shipment.shipTo.state}
              </span>
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-default-600">
              {t("createShipment.packages")}
            </span>
            <span className="font-medium font-mono text-xs">
              {shipment.packages.length} · {totalKg} kg{dims && ` · ${dims}`}
            </span>
          </div>
        </CardBody>

        <div className="px-6 py-4 border-t border-default-200 flex flex-col gap-2.5 text-sm">
          <div className="flex justify-between">
            <span className="text-default-600">
              {t("createShipment.shippingLabel")}
            </span>
            <span className="font-mono">
              {formatCurrency(labelCents, currency)}
            </span>
          </div>
          {pickupCents > 0 && (
            <div className="flex justify-between">
              <span className="text-default-600">
                {t("createShipment.pickup")}
              </span>
              <span className="font-mono">
                {formatCurrency(pickupCents, currency)}
              </span>
            </div>
          )}
          {pickupDiscountCents > 0 && (
            <div className="flex justify-between text-success">
              <span>{t("createShipment.pickupBundleDiscount")}</span>
              <span className="font-mono">
                −{formatCurrency(pickupDiscountCents, currency)}
              </span>
            </div>
          )}
          <div className="flex justify-between border-t border-default-200 pt-2 text-default-600">
            <span>{t("createShipment.subtotal")}</span>
            <span className="font-mono">
              {formatCurrency(subtotalCents, currency)}
            </span>
          </div>
          {taxCents > 0 && (
            <div className="flex justify-between">
              <span className="text-default-600">
                {t("createShipment.hst")}
              </span>
              <span className="font-mono">
                {formatCurrency(taxCents, currency)}
              </span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-default-200 bg-default-50 flex justify-between items-baseline">
          <span className="text-sm font-semibold text-default-700">
            {t("createShipment.total")}
          </span>
          <span className="text-2xl font-bold tracking-tight font-mono">
            {formatCurrency(grandTotalCents, currency)}
          </span>
        </div>
      </Card>

      <p className="mt-3 px-1 text-xs text-default-500 leading-relaxed">
        {t("createShipment.termsPrefix")}{" "}
        <a
          className="text-primary hover:underline"
          href={marketingUrl("/terms-of-use")}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("createShipment.termsLink")}
        </a>{" "}
        {t("createShipment.termsSuffix")}
      </p>
    </div>
  );
}

export default function CreateShipmentPaymentScreen() {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    data,
    update,
    createdOrder,
    setCreatedOrder,
    setCreatedShipments,
    discardDraft,
  } = useCreateOrder();
  const stepRef = useRef<PaymentStepHandle>(null);
  const [canSubmit, setCanSubmit] = useState(false);
  const [actionLabel, setActionLabel] = useState(
    t("createShipment.createOrder")
  );

  const createOrder = useCreateUnifiedOrder({
    onError: (error: unknown) => {
      const data = axios.isAxiosError<{
        error?: string;
        message?: string;
        balanceCents?: number;
        requiredCents?: number;
      }>(error)
        ? error.response?.data
        : undefined;

      // Credit is full-coverage-only; the wallet can also be frozen while a
      // deposit dispute is open. Both need a specific explanation, not a
      // generic failure.
      if (data?.error === "INSUFFICIENT_CREDIT") {
        toast({
          variant: "destructive",
          title: t("createShipment.notEnoughCreditTitle"),
          description:
            data.balanceCents !== undefined && data.requiredCents !== undefined
              ? t("createShipment.notEnoughCreditDetail", {
                  balance: formatCurrency(data.balanceCents),
                  required: formatCurrency(data.requiredCents),
                })
              : t("createShipment.notEnoughCreditGeneric"),
        });
        return;
      }
      if (data?.error === "CREDIT_WALLET_FROZEN") {
        toast({
          variant: "destructive",
          title: t("createShipment.creditUnavailableTitle"),
          description: t("createShipment.creditUnavailableBody"),
        });
        return;
      }

      toast({
        title: t("createShipment.createOrderErrorTitle"),
        description: data?.message ?? t("createShipment.createOrderError"),
        variant: "destructive",
      });
    },
  });

  const submitOrder = (draft: OrderDraft) => {
    const shipment = draft.shipment;
    if (!shipment) {
      logger.error("submitOrder: no shipment data", draft);
      return;
    }
    if (!draft.paymentMethodType || !draft.selectedRate) {
      toast({
        variant: "destructive",
        title: t("createShipment.createOrderErrorTitle"),
        description: t("createShipment.missingPaymentOrQuote"),
      });
      return;
    }

    // Send the parcel AS THE CUSTOMER ENTERED IT. The server converts, once.
    //
    // This used to convert to grams and centimetres here, which is what broke
    // checkout: the quote request sends the customer's own units, so the two
    // requests described the same parcel with different numbers and the server
    // refused the order as `wrong_shipment`.
    const packages = shipment.packages.map((pkg: PackageFormData) => ({
      weight: pkg.weight,
      weightUnit: pkg.weightUnit,
      length: pkg.length,
      width: pkg.width,
      height: pkg.height,
      dimensionUnit: pkg.dimensionUnit,
      packageKind: pkg.packageKind ?? "PARCEL",
      description: pkg.description,
      isFragile: pkg.isFragile,
      requiresCooling: pkg.requiresCooling,
      declaredValueCents: pkg.declaredValueCents,
    }));

    // Server (create-unified-order) requires EXACTLY ONE of
    // shipFromAddressBookEntryId / shipFrom per side (XOR refine). When the
    // user picked from the address book, the form's shipFrom is also populated
    // from the contact — so we must drop it and send only the entry ID.
    const useFromEntry = Boolean(draft.shipFromAddressBookEntryId);
    const useToEntry = Boolean(draft.shipToAddressBookEntryId);

    const request: UnifiedOrderRequest = {
      paymentMethodType: draft.paymentMethodType,
      paymentMethodId: draft.selectedPaymentMethodId,
      paymentTermsId: draft.paymentTermsId,
      shipments: [
        {
          selectedRate: draft.selectedRate,
          pickup: draft.includePickup ? draft.pickup : undefined,
          senderName: shipment.senderName,
          senderEmail: shipment.senderEmail,
          senderPhone: shipment.senderPhone,
          shipFrom: useFromEntry ? undefined : shipment.shipFrom,
          shipFromAddressBookEntryId: useFromEntry
            ? draft.shipFromAddressBookEntryId
            : undefined,
          recipientName: shipment.recipientName,
          recipientEmail: shipment.recipientEmail,
          recipientPhone: shipment.recipientPhone,
          shipTo: useToEntry ? undefined : shipment.shipTo,
          shipToAddressBookEntryId: useToEntry
            ? draft.shipToAddressBookEntryId
            : undefined,
          packages,
          deliveryNotes: shipment.deliveryNotes,
        },
      ],
    };

    createOrder.mutate(request, {
      onSuccess: (order: Order) => {
        setCreatedOrder(order);
        setCreatedShipments(order.shipments ?? []);
        discardDraft();
        void navigate("/new-shipment/confirmation");
      },
    });
  };

  const labelCents = data.selectedQuote?.labelPriceCents ?? 0;
  const labelTaxCents = data.selectedQuote?.taxCents ?? 0;
  const pickupTier =
    data.includePickup && data.pickup
      ? data.selectedQuote?.pickupOptions.find(
          (t) => t.pickupServiceId === data.pickup?.pickupServiceId
        )
      : undefined;
  const {
    pickupCents,
    discountCents: pickupDiscountCents,
    taxCents: pickupTaxCents,
  } = pickupDisplayCents(
    pickupTier,
    data.pickupBundleDiscountCents ?? 0,
    !!(data.includePickup && data.pickup)
  );
  const totalCents =
    labelCents +
    pickupCents -
    pickupDiscountCents +
    labelTaxCents +
    pickupTaxCents;
  const currency = data.selectedQuote?.currency ?? "CAD";

  // Render-time guard: redirect users who reach /payment without the prior
  // steps' data (deep-link / refresh). Done during render, not in a useEffect,
  // so it's synchronous (no flash) and can't be retriggered by an unrelated
  // state change — once createdOrder is set, onSuccess owns navigation to
  // /confirmation and discardDraft() emptying `data` must not bounce us.
  if (!createdOrder) {
    if (!data.shipment) return <Navigate to="/new-shipment/details" replace />;
    if (!data.selectedRate)
      return <Navigate to="/new-shipment/pricing" replace />;
  }

  return (
    <CreateShell
      step={3}
      title={t("createShipment.paymentTitle")}
      italic={t("createShipment.paymentItalic")}
      lede={t("createShipment.paymentLede")}
      footer={
        <>
          <div className="hidden md:flex items-center gap-4">
            <DraftSavedIndicator />
            <div className="text-sm text-default-500 pl-4 border-l border-default-200">
              {t("createShipment.total")} ·{" "}
              <span className="font-mono text-default-900 font-bold text-base">
                {formatCurrency(totalCents, currency)}
              </span>{" "}
              {currency}
            </div>
          </div>
          <div className="md:hidden flex justify-between items-baseline w-full">
            <span className="text-sm text-default-500">
              {t("createShipment.total")}
            </span>
            <span className="font-mono text-default-900 font-bold text-lg">
              {formatCurrency(totalCents, currency)}
            </span>
          </div>
          <div className="flex gap-2 md:gap-3 w-full md:w-auto">
            <Button
              variant="light"
              startContent={<ArrowLeft className="h-4 w-4" />}
              className="hidden md:inline-flex"
              onPress={() => navigate("/new-shipment/pricing")}
            >
              {t("createShipment.backToPricing")}
            </Button>
            <Button
              color="primary"
              fullWidth
              className="md:w-auto"
              isDisabled={!canSubmit || createOrder.isPending}
              isLoading={createOrder.isPending}
              onPress={() => stepRef.current?.submit()}
            >
              {actionLabel}
            </Button>
          </div>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">
        <div className="min-w-0">
          <PaymentSelectionStep
            ref={stepRef}
            data={data}
            onNext={(stepData) => {
              update(stepData);
              submitOrder({ ...data, ...stepData });
            }}
            onCanSubmitChange={setCanSubmit}
            onActionLabelChange={setActionLabel}
          />
        </div>
        <OrderSummaryCard data={data} />
      </div>
    </CreateShell>
  );
}
